// Direct port of the exportVPO function from
// app/javascript/app/pages/Vpo/New.jsx (lines 520–581).
//
// Produces an xlsx with two sheets:
//   1. "VPO"     — one row per visible product, with formulas:
//                  total_cost    = R * N      (col 15 = col 18 × col 14)
//                  total_selling = R * P      (col 17 = col 18 × col 16)
//                  Currency formatting on the price columns, integers on
//                  the quantity columns.
//   2. "Vendors" — a single-column list of vendor names, used as a data
//                  validation source for the "Vendor" column on sheet 1.
//
// The 1:1 column order (27 cols) matches the table render order on the
// New page so the export reads the same as the screen.

export interface VpoNewRow {
  customer: string | null
  brand: string | null
  customer_po_number: string | null
  order_id: string | null
  ghost_id: string | null
  state: string | null
  vendor: string | null
  vendor_po_number: string | null
  vendor_po_status: string | null
  upc: string | null
  style_number: string | null
  style_description: string | null
  color: string | null
  size: string | null
  cost_price: number | string | null
  total_cost: number | string | null
  selling_price: number | string | null
  total_selling: number | string | null
  original_order: number | string | null
  ordered: number | string | null
  bo: number | string | null
  confirmed: number | string | null
  received: number | string | null
  invoiced: number | string | null
  vendor_order_date: string | null
  vendor_start_ship_date: string | null
  vendor_cancel_date: string | null
  currency: string | null
}

const COLUMNS: { header: string; key: keyof VpoNewRow; width: number }[] = [
  { header: 'Customer',               key: 'customer',                width: 20 },
  { header: 'Brand',                  key: 'brand',                   width: 15 },
  { header: 'Customer PO#',           key: 'customer_po_number',      width: 15 },
  { header: 'Order ID',               key: 'order_id',                width: 10 },
  { header: 'Ghost Platform ID',      key: 'ghost_id',                width: 14 },
  { header: 'Status',                 key: 'state',                   width: 14 },
  { header: 'Vendor',                 key: 'vendor',                  width: 18 },
  { header: 'Vendor PO#',             key: 'vendor_po_number',        width: 14 },
  { header: 'Vendor PO Status',       key: 'vendor_po_status',        width: 16 },
  { header: 'UPC',                    key: 'upc',                     width: 14 },
  { header: 'Style number',           key: 'style_number',            width: 14 },
  { header: 'Style description',      key: 'style_description',       width: 22 },
  { header: 'Color',                  key: 'color',                   width: 10 },
  { header: 'Size',                   key: 'size',                    width: 8  },
  { header: 'Cost Price',             key: 'cost_price',              width: 11 },
  { header: 'Total Cost',             key: 'total_cost',              width: 12 },
  { header: 'Selling Price',          key: 'selling_price',           width: 12 },
  { header: 'Total Selling',          key: 'total_selling',           width: 13 },
  { header: 'Original Order',         key: 'original_order',          width: 12 },
  { header: 'Ordered',                key: 'ordered',                 width: 10 },
  { header: 'BO',                     key: 'bo',                      width: 8  },
  { header: 'Confirmed',              key: 'confirmed',               width: 10 },
  { header: 'Received',               key: 'received',                width: 10 },
  { header: 'Invoiced',               key: 'invoiced',                width: 10 },
  { header: 'Vendor Order Date',      key: 'vendor_order_date',       width: 14 },
  { header: 'Vendor PO Start Ship',   key: 'vendor_start_ship_date',  width: 18 },
  { header: 'Vendor PO Cancel Date',  key: 'vendor_cancel_date',      width: 18 },
]

export async function exportVpoNewToXlsx(rows: VpoNewRow[], vendors: string[]): Promise<void> {
  // Dynamic import keeps exceljs out of the initial bundle.
  const ExcelJS = (await import('exceljs')).default
  const FileSaver = (await import('file-saver')).default

  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'warehouse-app'
  workbook.created = new Date()

  const sheet = workbook.addWorksheet('VPO')
  sheet.columns = COLUMNS.map(c => ({ header: c.header, key: c.key, width: c.width }))

  // Currency / number formats — mirror the legacy XLSX styles.
  const currencyCols = ['cost_price', 'total_cost', 'selling_price', 'total_selling']
  const intCols      = ['original_order', 'ordered', 'bo', 'confirmed', 'received', 'invoiced']
  currencyCols.forEach(k => {
    const col = sheet.getColumn(k)
    if (col) col.numFmt = '$#,##0.00'
  })
  intCols.forEach(k => {
    const col = sheet.getColumn(k)
    if (col) col.numFmt = '0'
  })

  rows.forEach((row, idx) => {
    const excelRowIndex = idx + 2 // 1-indexed, plus header row
    const values: Record<string, unknown> = {}
    COLUMNS.forEach(c => { values[c.key as string] = (row as Record<string, unknown>)[c.key as string] })

    // Formulas for derived totals — quantity (col R = 18) × price (col N or P).
    // Cell coordinates: cost_price at column 15 (O), total_cost at 16 (P),
    // selling_price at 17 (Q), total_selling at 18 (R), original_order at 19 (S).
    // legacy used quantity × price; product_total_cost = original_order × cost_price
    values.total_cost    = { formula: `S${excelRowIndex}*O${excelRowIndex}`, result: row.total_cost ?? 0 }
    values.total_selling = { formula: `S${excelRowIndex}*Q${excelRowIndex}`, result: row.total_selling ?? 0 }

    sheet.addRow(values)
  })

  // Header styling — bold + light fill
  const headerRow = sheet.getRow(1)
  headerRow.font = { bold: true }
  headerRow.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
  })

  // Vendors sheet — single column data validation source
  if (vendors.length > 0) {
    const vendorSheet = workbook.addWorksheet('Vendors')
    vendorSheet.columns = [{ header: 'Vendor', key: 'name', width: 30 }]
    vendors.forEach(v => vendorSheet.addRow({ name: v }))

    // Apply list validation to the Vendor column (col 7 = G) for rows 2…rows+1
    const lastDataRow = rows.length + 1
    for (let r = 2; r <= lastDataRow; r += 1) {
      const cell = sheet.getCell(`G${r}`)
      cell.dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`=Vendors!$A$2:$A$${vendors.length + 1}`],
      }
    }
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const stamp = `${new Date().toISOString().replace(/[:.]/g, '-')}`
  FileSaver.saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `vpo_export_${stamp}.xlsx`)
}
